/**
 * Player frame (HUD) and the walk/run indicator.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* ── Player frame ─────────────────────────────────────────────────────────
   Top-LEFT, not top-right: bootstrap.ts appends the Sign-out button at
   top:12/right:12 with z-index 20, outside base-view's DOM, so anything
   placed in that corner from here sits underneath it. */
.bv-hud {
  /* NO z-index here, deliberately, and that is a change from what this file
     used to say. Until 2026-09-18 the column was z-index 1002 — above every
     panel scrim (1001) — so the action bar stayed reachable with a window
     open. Janeks: *"Option to show multiple menu modals at the same time.
     dont. Menu must be behind the overlay."* Opening a window now closes
     whichever one was up (ui/panels.ts), so the argument for keeping the menu
     on top has gone with it, and the menu drops under the dim like everything
     else the window covers.

     The layering moved onto the CHILDREN instead of staying here, because a
     z-index on this element makes it a stacking context and nothing inside can
     then escape it — and one thing has to: the arena pending badge, whose
     Cancel has a live queue behind it. Keeping the badge in normal flow inside
     this column is what makes it sit under the action bar at the right height
     without anyone re-deriving a wrapping column's height in a second file.

     Safe to un-stack: every absolutely positioned descendant already resolves
     against a nearer positioned ancestor (.bv-hud-avatar, .bv-hud-bar,
     .bv-ab-cell before it moved out), never against this element.

     The frame is pointer-events:none apart from its own buttons, so a click
     anywhere else still reaches whatever is behind. */
  position: absolute;
  top: max(12px, env(safe-area-inset-top)); left: max(12px, env(safe-area-inset-left));
  width: 356px;
  font: 12px/1.4 var(--font-technical);
  color: var(--bone); letter-spacing: 0.02em;
  pointer-events: none; text-shadow: 0 1px 2px rgba(0,0,0,0.6);
  /* A bare COLUMN since 2026-09-15 — no background, border, padding or blur of
     its own. The panel chrome moved onto .bv-hud-panel so the action bar can
     sit outside it with nothing behind it, per the ask. The portrait canvas
     follows the width via its own ResizeObserver.

     WIDTH. 356px was ARITHMETIC while the action bar lived in this column
     (7 x 44px buttons + 6 x 8px gaps). The bar moved to its own dock in the
     bottom-left corner on 2026-10-16, so nothing here depends on the number
     any more; it was kept rather than shrunk so the card's own layout did not
     reflow as a side effect of moving the buttons. Free to retune. */
  display: flex; flex-direction: column; gap: 8px;
}
/* Each block of the column carries the layer the column used to. 1002 is the
   old value and still means the same thing: above the world, above the
   minimap, below the banker's own confirm step. position:relative is what
   makes z-index apply at all — it changes no containing block, per the note
   above. */
.bv-hud > * { position: relative; z-index: 1002; }
/* ...and goes UNDER the scrim while a window is open. index.ts toggles this
   class from the frame loop off modalOpen(), beside the minimap's own
   predicate, so there is one reader of "a window owns the screen" rather than
   a hook at every open and close site. 1000 rather than 'auto' so the order
   against the world and the minimap is still stated, not inherited. */
.bv-hud-under-panel > * { z-index: 1000; }
/* The exception, and the reason the layering is on the children at all. The
   arena badge is a status indicator with a LIVE control in it — the queue's
   Cancel — and the queue is 4 to 70 seconds long, so burying it behind an
   inventory the player opened while waiting would strand them in it. Janeks
   asked for it to stay reachable. Above the scrims in both states. */
.bv-hud > [data-arena-status],
.bv-hud-under-panel > [data-arena-status] { z-index: 1003; }
/* The chrome the whole frame used to wear, now worn by the two blocks that
   still want it (player, supply) and not by the action bar. */
.bv-hud-panel {
  background: linear-gradient(165deg, rgba(42,33,22,0.92), rgba(26,20,16,0.9));
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  padding: 10px;
  box-shadow:
    inset 0 1px 0 rgba(255,235,200,0.1), inset 0 0 0 1px rgba(0,0,0,0.5),
    inset 0 -14px 24px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
}
.bv-hud-panel[hidden] { display: none; }
.bv-hud-main { display: grid; grid-template-columns: 68px 1fr; gap: 10px; }
/* Positioning context for the level badge. The portrait itself cannot be it:
   it is overflow:hidden to clip the canvas, which would crop exactly the part
   of the badge that is meant to hang over the edge. */
.bv-hud-avatar { position: relative; width: 68px; }
.bv-hud-portrait {
  position: relative; width: 68px; height: 92px; border-radius: var(--radius-well); overflow: hidden;
  border: 1px solid var(--line);
  background: radial-gradient(115% 85% at 50% 15%, #3a281a, var(--well));
  box-shadow: inset 0 0 12px rgba(0,0,0,0.55);
}
/* The portrait canvas must be sized by CSS. PlayerPortrait calls
   setSize(w, h, false), which deliberately leaves the style alone, and the
   renderer multiplies by devicePixelRatio (up to 2). With no rule here the
   canvas shows at its pixel size, so on a retina screen it is twice the box
   and overflow:hidden clips the model off toward the top-left. Sibling canvases
   (the paperdoll, the dialog viewer) already carry this rule. */
.bv-portrait-canvas { display: block; width: 100%; height: 100%; }
/* The level, as a number alone. Overhangs the portrait's bottom-left corner
   by a few pixels so it reads as sitting ON the frame rather than inside it —
   the drop shadow and the ring are what sell that, not the offset alone.
   Bottom-LEFT because the bars run down the portrait's right-hand side. */
.bv-hud-level-badge {
  position: absolute; left: -7px; bottom: -7px;
  min-width: 24px; height: 24px; box-sizing: border-box; padding: 0 4px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 999px; border: 1px solid var(--brass);
  background: radial-gradient(90% 90% at 35% 25%, #4a3318, #1d1510);
  color: var(--brass-hot); font: 700 12px var(--font-display);
  font-variant-numeric: tabular-nums; letter-spacing: 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.12);
}
.bv-hud-bars { display: flex; flex-direction: column; justify-content: center; }
.bv-hud-name {
  font: 700 13px var(--font-display); color: var(--bone);
  margin-bottom: 7px; letter-spacing: 0.04em;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bv-hud-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.bv-hud-label { color: var(--brass-bright); width: 26px; font-size: 10px; text-transform: uppercase; }
.bv-hud-bar {
  position: relative; flex: 1; height: 15px; border-radius: var(--radius-tape); overflow: hidden;
  background:
    radial-gradient(120% 140% at 8% 0%, rgba(122,70,30,0.22), transparent 55%),
    linear-gradient(180deg, #090604, var(--well) 60%, #17100a);
  border: 1px solid #54401f;
  box-shadow:
    inset 0 2px 4px rgba(0,0,0,0.85), inset 0 -1px 0 rgba(255,215,150,0.08),
    0 1px 0 rgba(255,215,150,0.12), 0 0 0 1px rgba(0,0,0,0.55);
}
/* Worn glass over every track: segment ticks every 10 percent, scuffed
   specks, one diagonal scratch. Sits above the fill and below the number. */
.bv-hud-bar::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; z-index: 1;
  background-image:
    repeating-linear-gradient(90deg, transparent 0, transparent calc(10% - 1px), rgba(0,0,0,0.45) calc(10% - 1px), rgba(0,0,0,0.45) 10%),
    linear-gradient(112deg, transparent 38%, rgba(255,235,200,0.14) 38.4%, transparent 39.2%),
    linear-gradient(104deg, transparent 71%, rgba(0,0,0,0.35) 71.4%, transparent 72.4%),
    radial-gradient(circle at 18% 60%, rgba(255,255,255,0.2) 0, transparent 1px),
    radial-gradient(circle at 57% 30%, rgba(0,0,0,0.5) 0, transparent 1px),
    radial-gradient(circle at 86% 70%, rgba(255,255,255,0.16) 0, transparent 1px);
  background-size: 100% 100%, 100% 100%, 100% 100%, 23px 15px, 31px 15px, 19px 15px;
}
/* Unarmoured: dim the track too, so an empty bar reads as "no gear" rather
   than as a bar that failed to fill. */
.bv-hud-bar-empty { border-color: rgba(122,90,48,0.22); background: #150f08; }
.bv-hud-fill {
  position: relative; height: 100%; transition: width 0.2s ease;
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.28), inset 0 -2px 3px rgba(0,0,0,0.45), 1px 0 0 rgba(0,0,0,0.6);
}
/* Gloss on the upper half, grime band along the bottom. */
.bv-hud-fill::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0.03) 48%, transparent 50%),
    linear-gradient(0deg, rgba(20,8,0,0.4), transparent 38%);
  mix-blend-mode: overlay;
}
/* ── Healing ────────────────────────────────────────────────────────────────
   A heal already moved the bar, because every fill transitions its width. What
   it did not do was READ as a heal: 0.2s is tuned for damage, where the point
   is that the drop is over before you can dwell on it, and the same 0.2s
   applied to regen ticking up looked like the number was simply wrong a moment
   ago.
   So a heal gets its own, slower sweep plus a brief lift in brightness — the
   fill visibly travels to the right instead of arriving. The class is added on
   the heal edge and removed when the sweep ends (see hud.ts), never left on:
   if it were, the next hit would drain at heal speed too. */
.bv-hud-fill-hp.is-healing {
  transition: width 0.75s cubic-bezier(0.22, 0.61, 0.36, 1), filter 0.75s ease;
  filter: brightness(1.45) saturate(1.15);
}
.bv-hud-fill-hp { background: linear-gradient(90deg, #5e2418, #8a3a2a 45%, var(--rust)); }
.bv-hud-fill-ar { background: linear-gradient(90deg, #33485a, #4a6478 45%, #8fb0c4); }
.bv-hud-fill-en { background: linear-gradient(90deg, #3a4424, var(--moss-dark) 45%, var(--moss)); }
.bv-hud-fill-xp { background: linear-gradient(90deg, #4a3210, #6a4a1c 45%, var(--brass-hot)); }
/* Every bar's own numbers, centred ON it rather than in a column beside it.
   Absolutely positioned over the fill, so the text does not move as the bar
   drains — a value that slides with the fill is unreadable while it changes.
   The heavy shadow is what keeps it legible over both the filled and the
   empty half. */
.bv-hud-inbar {
  position: absolute; inset: 0; z-index: 2; display: flex; align-items: center; justify-content: center;
  font: 700 10px var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--bone); letter-spacing: 0.04em; pointer-events: none;
  text-shadow: 0 1px 2px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.8);
}
/* Full width under the avatar + bars, so it reads as the whole survivor's
   progress rather than as a fourth stat of the same kind. */
.bv-hud-bar-xp { margin-top: 8px; }
.bv-hud-carry[hidden] { display: none; }
.bv-hud-carry-fill { height: 100%; transition: width 0.2s ease, background 0.2s ease; }
/* Label above, bar below (2026-09-15, asked for) rather than the label sitting
   in a 26px gutter beside the track. The slot readout rides the same header
   line instead of a third row of its own: it is the cap that usually binds
   (inventory.md rule 2 — weight only matters for heavy items), so it belongs
   next to the word, and stacking it under the bar cost a row for four words. */
.bv-hud-carry-head {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 3px;
}
.bv-hud-carry-head .bv-hud-label { width: auto; }
.bv-hud-carry-slots {
  font-size: 10px; color: var(--bone-dim); letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums; text-align: right;
}
/* Persistent Cash/Gold readout -- the vendor/shop panels' own balance line
   only exists while that panel is open; this is the always-visible one,
   fed by the character sheet on load and kept live by the same
   economy:updated push those panels use (see hud.ts). */
.bv-hud-wallet {
  display: flex; align-items: center; gap: 10px; margin-top: 6px; font-family: var(--font-technical);
  font-variant-numeric: tabular-nums; font-size: 11px;
}
.bv-hud-wallet[hidden] { display: none; }

/* The two currency chips. Janeks: "Make it larger and looking like
   labels/buttons, press on Gold -> shop, topup., press on cash -> Gold to
   Cash." So the NUMBER is the control, not a 16px plus sign beside it.

   .bv-hud is pointer-events:none so the frame never eats a camera drag —
   anything interactive inside it has to opt back in, or it renders perfectly
   and is simply dead to the mouse. That is how the old plus button and the
   energy refill both shipped unclickable. */
.bv-hud-chip {
  pointer-events: auto;
  display: inline-flex; align-items: center; gap: 5px;
  min-height: 44px; padding: 3px 9px 3px 7px; cursor: pointer;
  touch-action: manipulation;
  font: 700 13px/1.2 var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--bone); background: linear-gradient(170deg, var(--panel-a), var(--well));
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.45);
  transition: color 0.12s ease, border-color 0.12s ease, transform 0.08s ease;
}
.bv-hud-chip:hover { border-color: var(--brass); color: var(--bone); }
.bv-hud-chip:active { transform: translateY(1px); }
/* The mark each chip carries: a struck cent sign for Cash, the coin art for
   Gold. Both live INSIDE the control so the pair reads as two labelled values
   rather than as two numbers with a stray symbol between them. */
.bv-hud-chip-mark {
  font: 700 12px/1 var(--font-display); color: var(--bone-dim);
}
.bv-hud-chip-coin {
  width: 15px; height: 15px; display: block; flex: none;
  object-fit: contain;
  filter: drop-shadow(0 1px 1px rgba(0,0,0,0.7));
}
.bv-hud-chip-cash:hover { border-color: var(--moss); }
.bv-hud-chip-cash:hover .bv-hud-chip-mark { color: var(--moss); }
.bv-hud-chip-gold { color: var(--brass-hot); }
.bv-hud-chip-gold:hover { border-color: var(--brass-hot); }

/* Kept for anything still rendering a plain wallet line. */
.bv-hud-wallet-item { color: var(--bone-dim); }
.bv-hud-wallet-gold { color: var(--brass-hot); }
/* Buy Gold (wallet row) / Refill energy (EN row) — same small square-icon
   button, deliberately identical for both since they're the two premium-
   currency actions surfaced directly on the HUD (see hud.ts's docstring on
   why neither gets a confirm step — that's reserved for the Banker). */
.bv-hud-mini-btn {
  /* .bv-hud is pointer-events:none so the panel never eats a camera drag —
     which silently made these two buttons unclickable, since nothing turned
     it back on for them. Anything interactive inside the HUD must opt in. */
  pointer-events: auto; position: relative;
  width: 16px; height: 16px; flex: 0 0 auto; line-height: 1; font-size: 10px;
  color: var(--brass-hot); background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-tape); cursor: pointer; padding: 0;
}
.bv-hud-mini-btn::after {
  content: ''; position: absolute; inset: -14px;
}
.bv-hud-mini-btn:hover { border-color: var(--brass); }
/* Transient feedback for the two premium actions above — a receipt or an
   error, auto-hidden by hud.ts after a few seconds. One shared style for
   both (no success/error color split) — same convention as paperdoll's
   .bv-pd-msg, which doesn't distinguish either. */
.bv-hud-msg {
  margin-top: 6px; padding: 4px 8px; border-radius: var(--radius-tape);
  background: var(--well); border: 1px solid var(--line);
  color: var(--bone); font-size: 11px; font-family: var(--font-note);
}
/* ── Walk/run indicator ───────────────────────────────────────────────────
   Reflects the Capslock-toggled gait (character-animation-runtime.md §3.7).
   Sits immediately LEFT of the minimap (2026-09-15, asked for) rather than in
   the HUD's bottom corner — so its right offset has to clear the minimap's width
   plus its 12px inset, and has to follow it down at the narrow breakpoint or
   the two overlap. Run is the default/neutral brass; walk gets the moss
   highlight, so the current state reads without reading the word. */
.bv-gait {
  position: absolute; bottom: 12px; right: 196px; z-index: 5;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  pointer-events: none; color: var(--brass-bright);
  text-shadow: 0 1px 3px rgba(0,0,0,0.9);
}
.bv-gait[hidden] { display: none; }
/* Double the action bar's 20px glyph — this one is read at a glance from the
   corner of the eye, not hunted for with a cursor. */
.bv-gait-icon { width: 40px; height: 40px; }
/* The glyph is PAINTED, not drawn: a silhouette mask over a currentColor fill,
   so it takes whatever colour .bv-gait carries and the word under it inherits
   the same declaration. See hud.ts for why the art had to become a mask before
   this could work. The mask url arrives inline as --gait-mask, because this
   stylesheet is a static string and cannot see Vite hashed asset paths. */
.bv-gait-glyph {
  display: block; width: 100%; height: 100%;
  background-color: currentColor;
  -webkit-mask-image: var(--gait-mask); mask-image: var(--gait-mask);
  -webkit-mask-size: contain; mask-size: contain;
  -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
  -webkit-mask-position: center; mask-position: center;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.8));
}
.bv-gait-word {
  font: 700 11px/1 var(--font-display); letter-spacing: 0.12em; text-transform: uppercase;
}
.bv-gait-walk { color: var(--moss); }
/* Crouch is a third posture, not a slower walk. Cool grey (--duct) rather
   than a status token: --rust means danger and crouching is not danger,
   and every warm token reads as brass at 11px. Contrast against the other
   two states is the whole job here. NO BACKTICKS in this file's comments —
   the whole stylesheet is one template literal. */
.bv-gait-crouch { color: var(--duct); }
@media (max-width: 640px), (max-height: 480px) {
  .bv-gait { right: 142px; }
}
/* Beside the minimap, which stands on the action dock below 560px (see
   minimap.css.ts) — so this does too. */
@media (max-width: 560px) {
  .bv-gait { bottom: calc(20px + var(--bv-ab-dock-h, 0px)); }
}

/* ── Generated art (Higgsfield), 2026-10-14 ───────────────────────────────
   Janeks: no CSS drawings - the panel plate, the stat emblems, the level medal
   and the bar grain are all pictures. The URLs arrive as custom properties set
   by hud.ts (Vite hashes the file names). Every rule has a plain fallback, so
   a missing picture leaves the old look rather than a hole. */
.bv-hud-panel {
  background:
    linear-gradient(165deg, rgba(20,14,9,0.5), rgba(12,8,5,0.66)),
    var(--hud-plate, linear-gradient(165deg, rgba(42,33,22,0.92), rgba(26,20,16,0.9))) center / 100% 100% no-repeat;
  border-color: #5a4a30;
}
.bv-hud-emblem {
  width: 25px; height: 25px; margin: 0 1px 0 0; flex: none; display: block;
  object-fit: contain; pointer-events: none;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.75));
  transition: transform 0.2s cubic-bezier(0.3, 1.6, 0.5, 1);
}
.bv-hud-row:hover .bv-hud-emblem { transform: scale(1.14) rotate(-5deg); }
.bv-hud-row { min-height: 25px; margin-bottom: 4px; }
.bv-hud-row-xp { margin: 16px 0 0; }
.bv-hud-row-xp .bv-hud-bar-xp { margin-top: 0; }
/* Worn metal grain over every fill, under the gloss. */
.bv-hud-fill::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: var(--hud-grain, none) center / cover; mix-blend-mode: soft-light; opacity: 0.8;
}
/* The level badge is a brass medallion now; the number is stamped into it. */
.bv-hud-level-badge {
  left: -9px; bottom: -9px; min-width: 32px; height: 32px; padding: 0;
  border: 0; background: var(--hud-medal, radial-gradient(90% 90% at 35% 25%, #4a3318, #1d1510)) center / contain no-repeat;
  color: #f4e2b4; font-size: 13px; box-shadow: none;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.75));
  text-shadow: 0 1px 0 rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.8);
}

/* The one strip of hazard tape on the HUD: stuck across the player panel's top
   right corner, its other end peeled off and hanging, swaying. Same stripe
   recipe as the Skills tab (paperdoll.css.ts), so it is one tape, not two. */
.bv-hud-tape { position: absolute; right: 10px; top: -5px; width: 0; height: 0; pointer-events: none; z-index: 4; }
.bv-hud-tape-stuck {
  right: 0; top: -7px; width: 64px; height: 14px;
  transform-origin: right center; transform: rotate(-4deg);
}
.bv-hud-tape-fall {
  position: absolute; right: 64px; top: 0; width: 0; height: 0;
  transform-origin: 0 0; transform: rotate(98deg);
  animation: bvhud-tape-sway 6.3s ease-in-out infinite;
}
.bv-hud-tape-fall .bv-pd-tape-loose { width: 44px; }
@keyframes bvhud-tape-sway {
  0%, 100% { transform: rotate(98deg); }
  35%      { transform: rotate(110deg); }
  64%      { transform: rotate(92deg); }
}
@media (prefers-reduced-motion: reduce) { .bv-hud-tape-fall { animation: none; } .bv-hud-row .bv-hud-emblem { transition: none; } }
@media (max-width: 720px) {
  .bv-hud { width: clamp(200px, 50vw, 356px); }
}
@media (max-width: 640px) { .bv-hud-tape { display: none; } }
`;
